import { MapGoogleClusterRenderer } from "@/scripts/mapping/mapGoogleClusterRenderer";
import { MapLayerItemTypeIds } from "@/store/map-layers/types";

import {
	ClusterContextMenuResponse,
	IClusterRenderer,
	IMapEventListener,
	MapEventListenerEventNames,
	MapMarkerClustererOptions
} from "@sureview/v2-mapping-saas";
import { Component, Vue } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";

const MapStore = namespace("map");

/**
 * @summary Mixin which adds extended functionality to map marker clusterer.
 */
@Component
export default class MapMarkerClustererMixin extends Vue {
    private readonly defaultClusterMinZoom: number = 0;
    private readonly defaultClusterMaxZoom: number = 19;
    private readonly defaultClusterRadius: number = 250;
    private readonly maxZoomLevel: number = 30;
    private readonly sameLocationRadius: number = 0;

    @Getter("getFeature") getFeature: (featureName: string[]) => boolean;
    @MapStore.Mutation protected setClusterContextMenuResponse: (clusterContextMenuResponse: ClusterContextMenuResponse | null) => void;

    public isClusteringEnabled: boolean = true;
    public clusterMapEventListeners: IMapEventListener[] = [];
    public clusterMinZoom: number = this.defaultClusterMinZoom;
    public clusterMaxZoom: number = this.defaultClusterMaxZoom; // Building Level - Clusters when zoom level is <= clusterMaxZoom
    public clusterRadius: number = this.defaultClusterRadius;
    public defaultRenderer: IClusterRenderer | null = null;
    private mapLayerItemTypeIdsToCluster: number[] = [
		MapLayerItemTypeIds.Camera,
		MapLayerItemTypeIds.Audio,
		MapLayerItemTypeIds.Relay,
		MapLayerItemTypeIds.Alarm,
		MapLayerItemTypeIds.Output,
		MapLayerItemTypeIds.Area,
	];

    public get isMapStackedItemsFeatureEnabled(): boolean {
		return this.getFeature(["Map", "StackedItems"]);
	}

    public get isMapClusteringFeatureEnabled(): boolean {
		return this.getFeature(["Map", "Clustering"]);
	}

    public get isMapClusteringAllFeatureEnabled(): boolean {
		return this.getFeature(["Map", "Clustering", "All"]);
	}

    public isItemClusterable(mapLayerItemTypeId: number): boolean {
		return this.isMapLayerItemClusterable(mapLayerItemTypeId) || this.isCameraClusterable(mapLayerItemTypeId);
	}

    private isCameraClusterable(mapLayerItemTypeId: number): boolean {
		return this.isMapClusteringFeatureEnabled && mapLayerItemTypeId === MapLayerItemTypeIds.Camera;
	}

	private isMapLayerItemClusterable(mapLayerItemTypeId: number): boolean {
		return this.isMapClusteringAllFeatureEnabled && this.mapLayerItemTypeIdsToCluster.includes(mapLayerItemTypeId);
	}

    /**
     * @summary Computed getter for the map marker clusterer options.
     * @returns {MapMarkerClustererOptions} Returns the map marker clusterer options.
     */
    public get markerClustererOptions(): MapMarkerClustererOptions {
        return {
            isEnabled: this.isClusteringEnabled,
            mapEventListeners: this.clusterMapEventListeners,
            minZoom: this.clusterMinZoom,
            maxZoom: this.clusterMaxZoom,
            radius: this.clusterRadius,
            clusterRenderer: this.defaultRenderer,
        };
    }

    public setupMarkerClusterer(): void {
        this.isClusteringEnabled = this.isMapStackedItemsFeatureEnabled || this.isMapClusteringFeatureEnabled;

        if (this.isMapStackedItemsFeatureEnabled) {
            this.clusterMapEventListeners.push({
                eventName: MapEventListenerEventNames.ContextMenu,
                callback: this.handleClusterContextMenu,
            });
        }

        if (this.isMapStackedItemsFeatureEnabled && !this.isMapClusteringFeatureEnabled) {
            this.clusterRadius = this.sameLocationRadius;
        }

        this.defaultRenderer = new MapGoogleClusterRenderer(this.markerClustererOptions);
    }

    /**
     * @summary Handler for when the max zoom level is reached from a cluster click.
     */
    private handleClusterContextMenu(response: ClusterContextMenuResponse): void {
        this.setClusterContextMenuResponse(response);
    }
}
